import API_BASE_URL from './config';
import React, { useState, useEffect } from 'react';

function AdminRolling({ adminId }) {
  const [entries, setEntries] = useState([]);
  const [message, setMessage] = useState('');
  const [notes, setNotes] = useState({});
  const [results, setResults] = useState({});
  const [formData, setFormData] = useState({});

  useEffect(() => {
    const fetchEntries = async () => {
      try {
        const sportingRes = await fetch(`${API_BASE_URL}/dog-show/pending-sporting?adminId=${adminId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (!sportingRes.ok) throw new Error(await sportingRes.text());
        const sportingData = await sportingRes.json();

        const conformationRes = await fetch(`${API_BASE_URL}/dog-show/pending-conformation?adminId=${adminId}`, {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        });
        if (!conformationRes.ok) throw new Error(await conformationRes.text());
        const conformationData = await conformationRes.json();

        const combinedEntries = [...conformationData, ...sportingData];
        setEntries(combinedEntries);

        const initialFormData = {};
        combinedEntries.forEach(entry => {
          initialFormData[entry._id] = {
            fullBody: false,
            shading: false,
            background: false,
            handler: false
          };
        });
        setFormData(initialFormData);
      } catch (error) {
        setMessage('Failed to load entries: ' + error.message);
      }
    };
    fetchEntries();
  }, [adminId]);

  const handleCheckboxChange = (entryId, field) => {
    setFormData(prev => ({
      ...prev,
      [entryId]: {
        ...prev[entryId],
        [field]: !prev[entryId][field]
      }
    }));
  };

  const reviewEntry = async (entryId, status) => {
    const adminNotes = notes[entryId] || '';
    const entry = entries.find(e => e._id === entryId);
    const hasItem = !!entry.itemId;
    const isChampion = entry.isChampion;
    const { fullBody, shading, background, handler } = formData[entryId] || {};

    try {
      const res = await fetch(`${API_BASE_URL}/dog-show/review/${entryId}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ 
          adminId, 
          status: status === 'Rejected' ? 'Dismissed' : status, 
          adminNotes, 
          fullBody, 
          shading, 
          background, 
          handler, 
          hasItem, 
          isChampion,
          type: entry.type
        })
      });

      if (!res.ok) throw new Error(await res.text());
      const data = await res.json();
      setMessage(status === 'Approved' 
        ? `${data.message}: ${data.result}` 
        : data.message
      );
      if (data.result && data.ribbons) {
        setResults(prev => ({
          ...prev,
          [entryId]: { 
            result: data.result, 
            ribbons: data.ribbons, 
            awardPoints: data.awardPoints, 
            qualifyingScore: data.qualifyingScore 
          }
        }));
      }
      setEntries(entries.filter(e => e._id !== entryId));
      setNotes(prev => ({ ...prev, [entryId]: '' }));
    } catch (error) {
      setMessage('Review failed: ' + error.message);
    }
  };

  if (!adminId) return <p>Admin access required.</p>;

  return (
    <div style={{ padding: '20px', maxWidth: '800px', margin: '0 auto' }}>
      <h1>iCON Show Review</h1>
      {message && (
        <div style={{ backgroundColor: '#90EE90', padding: '10px', marginBottom: '20px', borderRadius: '5px' }}>
          {message}
        </div>
      )}
      {entries.length > 0 ? (
        <ul style={{ listStyle: 'none', padding: '0' }}>
          {entries.map(entry => (
            <li key={entry._id} style={{ marginBottom: '20px', border: '1px solid #ccc', padding: '10px', borderRadius: '5px' }}>
              <p><strong>Type:</strong> {entry.type === 'conformation' ? 'Conformation' : 'Sporting'}</p>
              <p><strong>Dog:</strong> {entry.dogInfo.registeredName} ({entry.dogInfo.breed}) - {entry.dogId} {entry.isChampion ? '(Champion)' : ''}</p>
              <div style={{ margin: '10px 0', textAlign: 'center' }}>
                <strong>Artwork:</strong>
                <a href={entry.artworkLink} target="_blank" rel="noopener noreferrer">
                  <img
                    src={entry.artworkLink}
                    alt="Submitted Artwork"
                    style={{
                      maxWidth: '100%',
                      maxHeight: '300px',
                      objectFit: 'contain',
                      borderRadius: '5px',
                      boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
                      display: 'block',
                      margin: '0 auto'
                    }}
                    onError={(e) => { e.target.src = '/path/to/fallback.png'; }}
                  />
                </a>
              </div>
              <p><strong>Fee Paid:</strong> {entry.entryFee} DFG$</p>
              <p><strong>Item Used:</strong> {entry.itemId ? `${entry.itemId.name} (${entry.itemId.description})` : 'None'}</p>
              <textarea
                value={notes[entry._id] || ''}
                onChange={(e) => setNotes({ ...notes, [entry._id]: e.target.value })}
                placeholder="Admin notes (required for dismissal)"
                style={{ width: '100%', marginTop: '10px' }}
              />
              <div style={{ marginTop: '10px' }}>
                <label style={{ marginRight: '15px' }}>
                  <input 
                    type="checkbox" 
                    checked={formData[entry._id]?.fullBody || false} 
                    onChange={() => handleCheckboxChange(entry._id, 'fullBody')} 
                  /> Full Body
                </label>
                <label style={{ marginRight: '15px' }}>
                  <input 
                    type="checkbox" 
                    checked={formData[entry._id]?.shading || false} 
                    onChange={() => handleCheckboxChange(entry._id, 'shading')} 
                  /> Shading
                </label>
                <label style={{ marginRight: '15px' }}>
                  <input 
                    type="checkbox" 
                    checked={formData[entry._id]?.background || false} 
                    onChange={() => handleCheckboxChange(entry._id, 'background')} 
                  /> Background
                </label>
                <label style={{ marginRight: '15px' }}>
                  <input 
                    type="checkbox" 
                    checked={formData[entry._id]?.handler || false} 
                    onChange={() => handleCheckboxChange(entry._id, 'handler')} 
                  /> Handler
                </label>
              </div>
              <div style={{ marginTop: '10px' }}>
                <button 
                  onClick={() => reviewEntry(entry._id, 'Rejected')} 
                  style={{ backgroundColor: '#ff4d4d', color: 'white', marginRight: '10px', padding: '8px 15px', border: 'none', borderRadius: '5px' }}
                >
                  Dismiss
                </button>
                <button 
                  onClick={() => reviewEntry(entry._id, 'Approved')} 
                  style={{ backgroundColor: '#4CAF50', color: 'white', marginLeft: '10px', padding: '8px 15px', border: 'none', borderRadius: '5px' }}
                >
                  Approve & Roll
                </button>
              </div>
              {results[entry._id] && (
                <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#f0f0f0', borderRadius: '5px' }}>
                  <p><strong>Result:</strong></p>
                  <pre style={{ whiteSpace: 'pre-wrap', margin: '0' }}>{results[entry._id].result}</pre>
                  <p><strong>Award Points:</strong> {results[entry._id].awardPoints || 0}</p>
                  {entry.type === 'sporting' && (
                    <p><strong>Qualifying Score:</strong> {results[entry._id].qualifyingScore ? 'Yes' : 'No'}</p>
                  )}
                  <p><strong>Ribbons Earned:</strong></p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px' }}>
                    {results[entry._id].ribbons.length > 0 ? (
                      results[entry._id].ribbons.map((ribbonUrl, index) => (
                        <img 
                          key={index} 
                          src={ribbonUrl} 
                          alt={`Ribbon ${index + 1}`} 
                          style={{ width: '50px', height: 'auto' }}
                          onError={(e) => { e.target.src = '/path/to/fallback.png'; }}
                        />
                      ))
                    ) : (
                      <p>No ribbons earned.</p>
                    )}
                  </div>
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p>No pending entries.</p>
      )}
    </div>
  );
}

export default AdminRolling;